import classNames from 'classnames';
import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as ChipConstants from 'ui-library/chips/ChipConstants';
import PreventParentClickWrapper from 'ui-library/wrappers/PreventParentClickWrapper';
import PreventParentScrollWrapper from 'ui-library/wrappers/PreventParentScrollWrapper';

import {TABLE_ROW_BODY_HEIGHT} from '../scrollable/ScrollableTableConstants';
import {ChipItem, SmartChipType} from './useSmartChipQuery';
import VerticalChipListWithSpacing from './VerticalChipListWithSpacing';

/**
 * Computes the total row height for tables when chips are rendered on separate lines,
 * which causes the total row height to be greater than the default value.
 * Keep in sync with chips.less
 */
export function getChipsRowHeight(numLinks: number, chipSize: string): number {
  let padding;
  const lineHeight = ChipConstants.CHIP_LINE_HEIGHT;
  // Add 1 to account for the "+n more" chip when we have more
  // than we can display all at once.
  const numDisplayedChips = Math.min(numLinks, ChipConstants.CHIP_DISPLAY_LIMIT_TABLE_ROW + 1);
  switch (chipSize) {
    case ChipSize.SMALL:
      padding = ChipConstants.CHIP_SMALL_PADDING;
      break;
    case ChipSize.MINI:
      padding = ChipConstants.CHIP_MINI_PADDING;
      break;
    case ChipSize.TABLE_CELL:
      padding = ChipConstants.CHIP_TABLE_CELL_PADDING;
      break;
    default:
      throw new Error(`Unexpected chip size: ${chipSize}`);
  }
  // 5 px to account for u-mini-spacing-top
  const chipHeight = 5 + lineHeight + 2 * padding;
  return numLinks > 0 ? numDisplayedChips * chipHeight : TABLE_ROW_BODY_HEIGHT;
}

export interface ChipListProps<T extends SmartChipType> {
  itemIds: Array<string | null>;
  maxChips: number;
  chipSize: ChipSize;
  showLessSize?: ButtonSize;
  showMoreSize?: ChipSize;
  showChipsAsLinks?: boolean;
  isVertical?: boolean;
  /** displayNameOverrides provides displayLabels to be used for each chip that override the live-fetched displayLabels. getArchiveItemName() will still be called on the provided name. */
  displayNameOverrides?: Array<string | null | undefined>;
  // getSecondaryText is used to render additional info in the chip next to the item name or displayLabel
  getSecondaryText?: (item: ChipItem<T>) => string | null;
  // When provided, viewAllOverflowLink is rendered instead of +x more expand chip
  viewAllOverflowLink?: JSX.Element;
}

function ChipList<T extends SmartChipType>(props: ChipListProps<T>): JSX.Element | null {
  assertAndContinue(
    props.displayNameOverrides == null || props.itemIds.length === props.displayNameOverrides.length,
    'Mismatched number of itemIds and displayNameOverrides'
  );
  const chips = props.itemIds.map((itemId, i) => (
    <BenchChip
      className={classNames({'chip-container': !props.isVertical})}
      key={`${itemId}.${i}`}
      id={itemId}
      isInaccessible={!itemId}
      size={props.chipSize}
      isLink={props.showChipsAsLinks}
      getSecondaryText={props.getSecondaryText}
      displayNameOverride={
        props.displayNameOverrides &&
        props.displayNameOverrides.length > 0 &&
        BenchlingConfig.ENABLE_FROZEN_CHIPS
          ? props.displayNameOverrides[i]
          : null
      }
    />
  ));
  return (
    <BaseChipList
      chips={chips}
      maxChips={props.maxChips}
      chipSize={props.chipSize}
      showLessSize={props.showLessSize}
      showMoreSize={props.showMoreSize}
      isVertical={props.isVertical}
      viewAllOverflowLink={props.viewAllOverflowLink}
    />
  );
}

ChipList.defaultProps = {
  maxChips: Number.MAX_SAFE_INTEGER,
  showChipsAsLinks: true,
  isVertical: true,
};

export interface BaseChipListProps {
  chips: Array<JSX.Element>;
  maxChips: number;
  chipSize: ChipSize;
  showLessSize?: ButtonSize;
  showMoreSize?: ChipSize;
  isVertical?: boolean;
  viewAllOverflowLink?: JSX.Element;
}

/*
 * Shows a list of chips for each item, capped at maxChips. If there are more items
 * than maxChips, {maxChips} items are rendered, with an overflow item showing
 * remaining number of items.
 *
 * The overflow item is clickable. When clicked, the list expands to show all chips in a
 * scrollable div, with a button at the bottom to "close" the list again.
 *
 * Callers should use ChipList, which is a thin wrapper around this component.
 */
export function BaseChipList(props: BaseChipListProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);

  if (props.chips.length === 0 || props.maxChips === 0) {
    return null;
  }

  function getWrappedChips(chips: Array<JSX.Element>, isVertical: boolean | undefined): JSX.Element {
    if (isVertical) {
      return <VerticalChipListWithSpacing chips={chips} />;
    }
    return <div>{chips}</div>;
  }

  if (expanded) {
    // Subtract 5 px so when we expand the list, it shows half
    // of a chip and it is obvious there are more.
    // 5 px was chosen somewhat arbitrarily, based on what makes it
    // look like the chip is "cut off" in the UI.
    const divHeight = props.isVertical ? getChipsRowHeight(props.chips.length, props.chipSize) - 5 : 'unset';
    const wrappedChips = getWrappedChips(props.chips, props.isVertical);

    const closeButton = (
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        onClick={(e) => {
          setExpanded(false);
        }}
        size={props.showLessSize ?? ButtonSize.MINI}
        text="Show less"
      />
    );

    return (
      <PreventParentScrollWrapper>
        <div
          className="scrollbar-override"
          style={{
            height: divHeight,
            overflowY: 'auto',
          }}
        >
          {wrappedChips}
          <div className="u-mini-spacing-top">
            <PreventParentClickWrapper>{closeButton}</PreventParentClickWrapper>
          </div>
        </div>
      </PreventParentScrollWrapper>
    );
  } else {
    const numOverflowChips = props.chips.length - props.maxChips;
    const displayedChips = props.chips.slice(0, props.maxChips);

    if (numOverflowChips > 0) {
      displayedChips.push(
        props.viewAllOverflowLink ?? (
          <Chip
            className={classNames({'chip-container': !props.isVertical})}
            onClick={(e) => {
              setExpanded(true);
            }}
            href="#"
            size={props.showMoreSize ?? ChipSize.MINI}
            text={`+${numOverflowChips} more`}
            key="overflow_chip"
          />
        )
      );
    }
    return getWrappedChips(displayedChips, props.isVertical);
  }
}

BaseChipList.defaultProps = {
  chips: [],
  maxChips: Number.MAX_SAFE_INTEGER,
  isVertical: true,
};

export default ChipList;
